/**
 * Generate a quarter's LCFS filing for CARB: the fuel transaction report and
 * the total-throughput form.
 *
 * The fuel transactions need nothing from the operator — the backend builds
 * them from the LCFS ledger. The throughput does: it is each station's TOTAL
 * natural gas dispensed in the quarter (fossil and renewable, every supplier),
 * a figure only the station operator has. Trillium sends it quarterly as its
 * station workbook, so the operator drops that file here.
 *
 * ## Trillium's FSE ids are not the ones CARB files under
 *
 * The workbook lists Trillium's own identifier per station. The preview maps
 * each onto the roster's; one the roster does not know is shown with a station
 * picker rather than guessed at — Trillium renumbers stations (LA Metro was
 * `FSE10317NS03`, then `FSE10317NS16`).
 *
 * ## Generate stays disabled until the quarter can be filed
 *
 * Every station on the fuel-transaction report needs a throughput figure, and
 * none can have dispensed less in total than Aemetis's own volume there. A
 * station the file has no row for can be typed in. The backend refuses the same
 * cases; the button only saves a round trip.
 *
 * ## A shortfall is filed only once someone has ticked it
 *
 * Claimed station volume the dairies' lots did not cover has no pathway, so it
 * drops out of the report. The backend refuses to file it unless each shortfall
 * is acknowledged — its month, station and exact MMBtu, as this preview shows
 * them — and records who did. Q2 2026's May Gardena claim is the case this
 * exists for: the month's production was transcribed short, and the quarter
 * must still be fileable while that is re-derived.
 */

import { Decimal, formatDecimal, parseDecimalString } from "@compliance/domain";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconFileSpreadsheet, IconUpload } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useGenerateLcfsQuarter, usePreviewLcfsThroughput } from "../../api/hooks";
import type { LcfsThroughputPreview } from "../../api/types";
import { useBranding } from "../../auth/useBranding";
import { formatApiError } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function therms(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  try {
    return formatDecimal(parseDecimalString(value), { decimalPlaces: 3 });
  } catch {
    return value;
  }
}

function parseDecimal(value: string): Decimal | null {
  const trimmed = value.replaceAll(",", "").trim();
  if (trimmed === "" || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  return new Decimal(trimmed);
}

type StationLine = {
  fse_id: string;
  station_name: string;
  ledger: string;
  /** Throughput from the file, or null when the operator must type it. */
  fromFile: string | null;
};

/** The stations on this quarter's report, each with where its throughput comes from. */
function reportStations(
  preview: LcfsThroughputPreview,
  picks: Record<string, string | null>,
): StationLine[] {
  const ledger = new Map<string, { name: string; therms: string }>();
  for (const row of preview.rows) {
    if (row.fse_id && row.ledger_customer_therms != null) {
      ledger.set(row.fse_id, {
        name: row.station_name ?? row.fse_id,
        therms: row.ledger_customer_therms,
      });
    }
  }
  for (const m of preview.missing) {
    ledger.set(m.fse_id, { name: m.station_name, therms: m.ledger_customer_therms });
  }
  const fromFile = new Map<string, string>();
  for (const row of preview.rows) {
    const id = row.fse_id ?? picks[row.partner_fse_id] ?? null;
    if (id) fromFile.set(id, row.total_throughput_therms);
  }
  return [...ledger.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fse_id, v]) => ({
      fse_id,
      station_name: v.name,
      ledger: v.therms,
      fromFile: fromFile.get(fse_id) ?? null,
    }));
}

export function LcfsQuarterModal({
  opened,
  onClose,
  year,
  quarter,
  label,
}: {
  opened: boolean;
  onClose: () => void;
  year: number;
  quarter: number;
  label: string;
}) {
  const preview = usePreviewLcfsThroughput();
  const generate = useGenerateLcfsQuarter();
  const queryClient = useQueryClient();
  // The partner's name is the acting organization's, never a constant: the
  // frontend-only demo ships this screen under a fictional dispenser.
  const partner = useBranding().dispensing_partner_label;
  const [file, setFile] = useState<File | null>(null);
  const [picks, setPicks] = useState<Record<string, string | null>>({});
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [acked, setAcked] = useState<Record<string, boolean>>({});

  const data = preview.data ?? null;
  const lines = useMemo(() => (data ? reportStations(data, picks) : []), [data, picks]);

  const resolved = lines.map((line) => {
    const raw = line.fromFile ?? typed[line.fse_id] ?? "";
    const value = parseDecimal(raw);
    const ledger = new Decimal(line.ledger);
    const problem = value == null ? "missing" : value.lt(ledger) ? "below" : null;
    return { ...line, raw, value, problem };
  });
  const shortfalls = data?.shortfalls ?? [];
  const stationNames = new Map((data?.stations ?? []).map((s) => [s.fse_id, s.station_name]));
  const allAcked = shortfalls.every((s) => acked[shortfallKey(s)]);
  const ready =
    data != null && resolved.length > 0 && resolved.every((r) => r.problem == null) && allAcked;

  const reset = () => {
    preview.reset();
    generate.reset();
    setFile(null);
    setPicks({});
    setTyped({});
    setAcked({});
  };
  const close = () => {
    reset();
    onClose();
  };

  const onDrop = async (files: File[]) => {
    const dropped = files[0];
    if (!dropped) return;
    reset();
    setFile(dropped);
    try {
      await preview.mutateAsync({ year, quarter, file: dropped });
    } catch {
      // Rendered from preview.error below.
    }
  };

  const onGenerate = async () => {
    try {
      await generate.mutateAsync({
        year,
        quarter,
        throughput: resolved.map((r) => ({ fse_id: r.fse_id, therms: r.value?.toString() ?? "" })),
        acknowledgedShortfalls: shortfalls.map((s) => ({
          period: s.period,
          fse_id: s.fse_id,
          mmbtu: s.mmbtu,
        })),
      });
      notifications.show({
        title: "LCFS report generated",
        message: `${label} fuel transactions, total throughput and support package are ready to download.`,
        color: "teal",
        autoClose: 5000,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.quarters.reports(year, quarter) });
      close();
    } catch {
      // Rendered from generate.error below.
    }
  };

  const unknownRows = data?.rows.filter((r) => r.fse_id == null) ?? [];
  const stationOptions = (data?.stations ?? []).map((s) => ({
    value: s.fse_id,
    label: `${s.station_name} (${s.fse_id})`,
  }));

  return (
    <Modal opened={opened} onClose={close} title={`LCFS quarterly report — ${label}`} size="xl">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          The fuel transactions are built from the LCFS ledger. Drop {partner}&apos;s quarterly
          station workbook for each station&apos;s total throughput. The utility statements and
          transaction confirmations filed for the quarter are bundled beside them, with their upload
          log.
        </Text>

        <Dropzone
          onDrop={onDrop}
          accept={[XLSX_MIME]}
          multiple={false}
          loading={preview.isPending}
          aria-label={`${partner} quarterly station workbook`}
          data-tour="lcfs-quarter-dropzone"
        >
          <Group justify="center" gap="sm" mih={64} style={{ pointerEvents: "none" }}>
            <Dropzone.Accept>
              <IconUpload size={28} />
            </Dropzone.Accept>
            <Dropzone.Idle>
              <IconFileSpreadsheet size={28} />
            </Dropzone.Idle>
            <Text size="sm">{file ? file.name : `Drop ${partner}'s station workbook (.xlsx)`}</Text>
          </Group>
        </Dropzone>

        {preview.error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Could not read the file">
            {formatApiError(preview.error)}
          </Alert>
        ) : null}

        {data ? (
          <>
            {unknownRows.length > 0 ? (
              <Alert color="yellow" title="Stations the roster does not recognise">
                <Stack gap="xs">
                  <Text size="sm">
                    Pick the station each of {partner}&apos;s ids belongs to, or leave it if it is
                    not on this quarter&apos;s report.
                  </Text>
                  {unknownRows.map((row) => (
                    <Group key={row.partner_fse_id} gap="sm" wrap="nowrap">
                      <Text size="sm" w={260}>
                        {row.station} · <code>{row.partner_fse_id}</code>
                      </Text>
                      <Select
                        aria-label={`Station for ${row.partner_fse_id}`}
                        placeholder="Pick a station"
                        data={stationOptions}
                        searchable
                        clearable
                        value={picks[row.partner_fse_id] ?? null}
                        onChange={(v) => setPicks((p) => ({ ...p, [row.partner_fse_id]: v }))}
                        style={{ flex: 1 }}
                      />
                    </Group>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            <Table withTableBorder striped verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Station</Table.Th>
                  <Table.Th>FSE ID</Table.Th>
                  <Table.Th ta="right">Ledger (therms)</Table.Th>
                  <Table.Th ta="right">Total throughput (therms)</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {resolved.map((r) => (
                  <Table.Tr key={r.fse_id}>
                    <Table.Td>{r.station_name}</Table.Td>
                    <Table.Td>
                      <code>{r.fse_id}</code>
                    </Table.Td>
                    <Table.Td ta="right">{therms(r.ledger)}</Table.Td>
                    <Table.Td ta="right">
                      {r.fromFile != null ? (
                        therms(r.fromFile)
                      ) : (
                        <TextInput
                          aria-label={`Total throughput for ${r.fse_id}`}
                          placeholder="Not in the file — type it"
                          size="xs"
                          value={typed[r.fse_id] ?? ""}
                          onChange={(e) => {
                            const v = e.currentTarget.value;
                            setTyped((t) => ({ ...t, [r.fse_id]: v }));
                          }}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      {r.problem === "missing" ? (
                        <Badge color="yellow" variant="light">
                          needs a figure
                        </Badge>
                      ) : r.problem === "below" ? (
                        <Badge color="red" variant="light">
                          below ledger volume
                        </Badge>
                      ) : (
                        <Badge color="teal" variant="light">
                          ok
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Text size="sm" c="dimmed">
              Ledger total {therms(data.ledger_total_therms)} therms
              {data.partner_customer_total_therms != null
                ? ` · ${partner}'s figure for us ${therms(data.partner_customer_total_therms)} therms`
                : ""}
            </Text>

            {shortfalls.length > 0 ? (
              <Alert color="yellow" title="Volume the dairies did not cover">
                <Stack gap="xs">
                  <Text size="sm">
                    This volume has no pathway, so it is left off the fuel-transaction report. Tick
                    each one to file the quarter without it. Your choice is recorded with the
                    report.
                  </Text>
                  {shortfalls.map((s) => {
                    const key = shortfallKey(s);
                    const name = stationNames.get(s.fse_id);
                    return (
                      <Checkbox
                        key={key}
                        id={`ack-${key}`}
                        checked={acked[key] ?? false}
                        onChange={(e) => {
                          const checked = e.currentTarget.checked;
                          setAcked((prev) => ({ ...prev, [key]: checked }));
                        }}
                        label={`File without ${therms(s.mmbtu)} MMBtu at ${
                          name ? `${name} (${s.fse_id})` : s.fse_id
                        } in ${s.period}`}
                      />
                    );
                  })}
                </Stack>
              </Alert>
            ) : null}
          </>
        ) : null}

        {generate.error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Generation failed">
            {formatApiError(generate.error)}
          </Alert>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={onGenerate}
            disabled={!ready}
            loading={generate.isPending}
            data-tour="lcfs-quarter-generate"
          >
            Generate LCFS report
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** One shortfall as the backend keys its acknowledgement: month, station, exact MMBtu. */
function shortfallKey(s: { period: string; fse_id: string; mmbtu: string }): string {
  return `${s.period}|${s.fse_id}|${s.mmbtu}`;
}
